import {AudioId} from "./types";
import {partition} from "./util";

export const soundData = (s?: string) =>
    s ? s.split(",").map(s => s.trim()) : [];


export const audioId = (s: AudioId): AudioId => (s.startsWith("!") || s.startsWith(">") || s.startsWith("#")) ? s.substring(1) : s;

export const fadeValue = (e: SlideEvent,a: 'fade-out-speed'| "fade-in-speed") => {
    const s = e.currentSlide.getAttribute("data-sounds-" + a) || e.currentSlide.getAttribute(a);
    return s ? parseInt(s, 10) : 1500
}

// State
let persistentAudios: string[] = []
// end State

/**
 * Determines which audio files to stop and start when transitioning between slides.
 *
 * This function analyses the sound data from the current and next slides to calculate
 * which audio files need to be stopped and which need to be started (or restarted).
 * It handles special operators that modify audio behaviour:
 *
 * Operators:
 * - `!id`: Restart operator - forces the audio to stop and restart from the beginning
 * - `>id`: Persistent start operator - starts audio that continues across slides until explicitly stopped with `#`
 * - `#id`: Persistent stop operator - stops a persistently playing audio
 * - `id`: No operator - continues playing if already active, or starts if new
 *

 *
 * State Management:
 * - Maintains global `persistentAudios` array tracking sounds started with `>` operator
 * - Persistent sounds continue playing across slides until stopped with `#` operator
 *
 * Examples:
 * - ["abc", "def"] → ["abc", "toto"]: stops "def", starts "toto"
 * - ["abc"] → ["!abc"]: stops nothing, restarts "abc"
 * - ["abc"] → [">def"]: continues "abc", starts "def" persistently
 * - ["abc", ">def"] → ["abc", "#def"]: continues "abc", stops "def"
 *
 * @param currentSounds - Sound IDs from the current slide (may include operators)
 * @param nextSounds - Sound IDs from the next slide (may include operators)
 * @returns A tuple of [soundsToStop, soundsToStart] where both are arrays of audio IDs (without operators)
 */
export const nextAudioActions = (currentSounds: string[], nextSounds: string[]): [string[], string[], string[]] => {
    console.log("current/next sounds", currentSounds, nextSounds);
    const [toRestart, nextToStartIds]: [string[], string[]] = partition(nextSounds, e => e.startsWith("!"));

    const persistentToStop = nextToStartIds.filter(e => e.startsWith("#")).map(audioId);
    const persistentStart = nextToStartIds.filter(e => e.startsWith(">")).map(audioId);
    persistentAudios = [...persistentAudios, ...persistentStart] // add to global state

    const currentSoundIds = currentSounds.map(audioId);
    const nextSoundsIds = nextSounds.map(audioId);
    const toRestartIds = toRestart.map(audioId);
    const toStop = [
        ...currentSoundIds
            .filter(e => !nextToStartIds.includes(e)) // remove the ones that carry over
            .filter(e => !toRestartIds.includes(e)) // remove the ones that need restarting
            .filter(e => persistentAudios.indexOf(e) === -1),
        ...persistentToStop]
    const toStart = [
        ...nextSoundsIds
            .filter(e => !currentSoundIds.includes(e)) // add new ones not carried over
            .filter(e => !persistentToStop.includes(e)), // remove the ones intended to stop
        ...toRestartIds, // add the ones that need restarted
    ]
    // keep playing are the ones prev and cur have in common, and remove toStart (those need a restart so are in toStart)
    const commonSounds = currentSoundIds.filter(id => nextSoundsIds.includes(id)).filter(id => !toStart.includes(id))
    console.log("toStop:", toStop, "toStart", toStart, "keep", commonSounds)
    return [toStop, toStart, commonSounds]
}
